import { ICellValue, ITableRow, ITableColumn, IFile, ITableViewGroup } from 'types'
import { GROUP_KEY_SPLIT } from 'components/spreadsheet/constants/const'
import { GroupByDictionary, IColumnTypes } from 'components/spreadsheet/types'
import { coerceToValue, getRowValue } from 'components/spreadsheet/helpers/functions'
import { CompressedColumnStore } from 'components/spreadsheet/helpers/compressed-column-store'
import { recordListsEqual } from 'components/spreadsheet/helpers/record-equality'

// Value-based comparison of two lists of group settings, insensitive to object key
// order. Used for the "unsaved changes" flag so that grouping then ungrouping a
// column (a net-zero change) returns the view to a clean state. recordListsEqual
// compares the union of keys, so a new attribute added to ITableViewGroup is picked
// up automatically without editing this helper.
export const areGroupSettingsEqual = (a: ITableViewGroup[], b: ITableViewGroup[]): boolean => {
  return recordListsEqual(a as unknown as Record<string, unknown>[], b as unknown as Record<string, unknown>[])
}

export const NO_VALUE_KEY = 'EmptyCellValue'

// The order of keys in an object are guaranteed in JS if the keys are strings or Symbols.
// So we use the prefix here to cause the keys to be strings. Strings that are integers
// are interpreted as integers so just calling the toString() method won't work.
export const GROUP_KEY_PREFIX = '_'

// Create a key for the attachements columns
export const createAttachmentColumnKey = (value: IFile[]): string => {
  let groupKey = ''
  if (value && Array.isArray(value) && value.length > 0) {
    const filenames = value.map((file) => file.filename).join(',')
    groupKey = `${filenames}`
  } else {
    groupKey = NO_VALUE_KEY
  }
  return groupKey
}

// Give a cell value, this will return the grouping key used for it.
// The grouping key must be a string
export const createGroupKey = (value: ICellValue, kind: IColumnTypes): string => {
  let key = ''
  if (value !== undefined && kind === 'attachment') {
    key = createAttachmentColumnKey(value as IFile[])
    return key
  }

  const v = coerceToValue(value, kind)
  if (typeof v === 'string') {
    key = v
  } else {
    key = v.toString()
  }
  key = key.trim()
  return key
}

// This is the grouping function for rows
export const rowGrouper = (
  rows: ITableRow[],
  column: ITableColumn | undefined,
  columnIdToIndex?: Map<string, number>,
  columnStore?: CompressedColumnStore | null
) => {
  return rows.reduce(
    (prev, current) => {
      const value = column && columnIdToIndex ? getRowValue(current, column.publicId, columnIdToIndex, columnStore) : ''
      const groupKey = value === null || column === undefined ? NO_VALUE_KEY : createGroupKey(value, column.kind)
      const groupKeyWithPrefix = `${GROUP_KEY_PREFIX}${groupKey}`

      if (!Object.prototype.hasOwnProperty.call(prev, groupKeyWithPrefix)) {
        prev[groupKeyWithPrefix] = []
      }
      prev[groupKeyWithPrefix].push(current)
      return prev
    },
    {} as Record<string, Array<ITableRow>>
  )
}

// This finds all the grouping keys for a column
export const getGroupingKeysForColumn = (
  rows: ITableRow[],
  groupByColumns: ITableColumn[],
  columnId: string,
  columnIdToIndex: Map<string, number>,
  columnStore?: CompressedColumnStore | null
) => {
  if (rows.length === 0) return new Set<string>()
  if (groupByColumns.length === 0) return new Set<string>()

  const columnGroupKeys = new Set<string>()

  // Find the target column's depth; if it's not in the list, nothing to do
  const targetIdx = groupByColumns.findIndex((c) => c.publicId === columnId)
  if (targetIdx === -1) return columnGroupKeys

  const groupRowsAtDepth = (rowsAtLevel: ITableRow[], depth: number, parentGroupKeys: string[]) => {
    const currentCol = groupByColumns[depth]
    // Safety guard (shouldn't happen if depth is controlled correctly)
    if (!currentCol) return

    const groups = rowGrouper(rowsAtLevel, currentCol, columnIdToIndex, columnStore)
    for (const [key, childRows] of Object.entries(groups)) {
      const newKeys = [...parentGroupKeys, key]

      if (depth === targetIdx) {
        // We reached the column we're collecting keys for
        columnGroupKeys.add(newKeys.join(GROUP_KEY_SPLIT))
      } else if (childRows.length > 0) {
        // Recurse only if there are deeper columns to traverse
        if (depth + 1 < groupByColumns.length) {
          groupRowsAtDepth(childRows, depth + 1, newKeys)
        }
      }
    }
  }

  // Kick off from the top level
  if (groupByColumns.length > 0) {
    groupRowsAtDepth(rows, 0, [])
  }

  return columnGroupKeys
}

export const getGroupRowsFromKeys = (groupKeys: string[], groupedRows: GroupByDictionary) => {
  let rows: ITableRow[] = []
  let group: any = undefined

  for (let i = 0; i < groupKeys.length; i++) {
    const key = groupKeys[i].split(GROUP_KEY_SPLIT)[i]
    if (group === undefined) {
      group = groupedRows[key]
      rows = group.childRows
    } else {
      group = group.childGroups[key]
      rows = group.childRows
    }
  }
  return rows
}

/**
 * Returns the public ID of the first non-grouped column in tree group view,
 * or null when tree group view is inactive. This column renders inside the
 * tree area and must not be hidden.
 */
export const getTreeLeafColumnId = (
  columns: { publicId: string }[],
  groupSettings: ITableViewGroup[],
  treeGroupView: boolean
): string | null => {
  if (!treeGroupView || groupSettings.length === 0) return null
  const groupedIds = groupSettings.map((g) => g.columnId)
  const firstNonGrouped = columns.find((col) => !groupedIds.includes(col.publicId))
  return firstNonGrouped?.publicId ?? null
}

// Every row across the top-level groups, in group order. Appends row by row: re-concatenating
// the accumulator per group copies it each time, which is quadratic in the number of groups and
// hangs the page when a column with many distinct values (document numbers, dates) is grouped.
export const getAllGroupRows = (groupedRows: GroupByDictionary): ITableRow[] => {
  const rows: ITableRow[] = []
  for (const key in groupedRows) {
    const childRows = groupedRows[key].childRows
    for (let i = 0; i < childRows.length; i++) rows.push(childRows[i])
  }
  return rows
}
